import { Injectable } from "@angular/core";
import { Browser } from "@capacitor/browser";
import { Platform } from "@ionic/angular/standalone";
import { InAppBrowserProvider } from "../../api/in-app-browser/in-app-browser.api";
import { AppConfig } from "../../app.config";
import { Constants } from "../../const/constants";
import { Gmo3dSecure } from "../../models/gmo-3d-secure";
import { PurchasePointHistory } from "../../models/purchase-point-history";
import { IosAppService } from "../ios-app/ios-app.service";
import { Util } from "../util/util";

const ACCOUNTKIT_APP_ID: string = AppConfig.META_DATA.accountKitAppID;
const REDIRECT_URL: string =
  window.location.origin + AppConfig.META_DATA.accountKitRedirectUrl;

@Injectable({
  providedIn: "root",
})
export class WebViewProvider {
  META_DATA = AppConfig.META_DATA;
  constructor(
    private iab: InAppBrowserProvider,
    private platform: Platform,
    private patolarService: IosAppService
  ) {}

  async showPrivacyPolicy(): Promise<void> {
    await this.showSafariView(this.META_DATA.privacypolicy);
  }

  async showTermsMemberGuest(): Promise<void> {
    await this.showSafariView(this.META_DATA.terms);
  }

  showTerms(): void {
    if (this.META_DATA.appID === "member") {
      this.showSafariView(this.META_DATA.terms);
    } else {
      this.showSafariView(this.META_DATA.termsPato);
    }
  }

  async showTermsBank(): Promise<void> {
    await this.showSafariView(this.META_DATA.termsBank);
  }

  async showPrivacy(): Promise<void> {
    await this.showSafariView(this.META_DATA.privacypolicy);
  }

  async showFAQ(type: "guest" | "cast"): Promise<void> {
    if (type === "guest") {
      await this.showSafariView(this.META_DATA[type + "_faq"]);
    } else {
      await this.showSafariView(this.META_DATA.question);
    }
  }

  async showTopCastLoad(): Promise<void> {
    await this.showSafariView(this.META_DATA.pato_perfect_manual);
  }

  async showManagerAssignedLink(): Promise<void> {
    await this.showSafariView(this.META_DATA.manager_assigned);
  }

  async showBubbleRushLine(): Promise<void> {
    await this.showSafariView(this.META_DATA.bubble_rush_line);
  }

  async showMidnightRestaurant(): Promise<void> {
    await this.showSafariView(Constants.Notion.MidnightRestaurant);
  }

  async showSMSFAQ(): Promise<void> {
    await this.showSafariView(this.META_DATA.sms_faq);
  }

  async showPatoAndroidApp(): Promise<void> {
    await this.showSafariView(this.META_DATA.pato_app_android);
  }

  async showHelpDescription(): Promise<void> {
    await this.showSafariView(this.META_DATA.help_description);
  }

  async showFinalReturn(): Promise<void> {
    await this.showSafariView(this.META_DATA.kakutei_shinkoku);
  }

  async showFavoriteList(): Promise<void> {
    await this.showSafariView(this.META_DATA.favorite_list);
  }

  async showMedia(): Promise<void> {
    await this.showSafariView(
      "https://www.notion.so/patoinformation/6b32a461ba0c410d94b5f378ef3bd295"
    );
  }

  async showASCT(): Promise<void> {
    await this.showSafariView(this.META_DATA.asct);
  }

  async showCastList(): Promise<void> {
    await this.showSafariView(this.META_DATA.officialURL + "cq_list");
  }

  getPlatform(): string {
    let plt = "";
    if (AppConfig.IS_BROWSER) {
      if (Util().isPatolar()) {
        plt = "patolar";
      } else if (Util().isPatoBrowser()) {
        plt = "pato_browser";
      } else {
        plt = "web";
      }
    } else {
      if (this.platform.is("ios")) {
        plt = "ios";
      } else {
        plt = "android";
      }
    }
    return plt;
  }

  showGMO3DSecureRedirectPage(
    gmo3dSecure: Gmo3dSecure,
    hash: string,
    state: string,
    callback: () => void = () => {}
  ): void {
    const plt: string = this.getPlatform();
    // 3Dsecure2.0の場合のredirectUrl
    let url = "";
    if (gmo3dSecure.redirectUrl) {
      // 3Dセキュア2.0以降の場合
      url = gmo3dSecure.redirectUrl;
    } else {
      // 3Dセキュア1.0の場合

      url =
        this.META_DATA.gmo3dSecureRedirectUrl +
        "?acs_url=" +
        encodeURIComponent(gmo3dSecure.acsUrl) +
        "&md=" +
        encodeURIComponent(gmo3dSecure.md) +
        "&pa_req=" +
        encodeURIComponent(gmo3dSecure.paReq) +
        "&term_url=" +
        encodeURIComponent(
          this.META_DATA.gmo3dSecureCallbalUrl +
            "?plt=" +
            plt +
            "&hash=" +
            hash +
            "&state=" +
            state
        );
    }

    if (Util().isPatolar() || Util().isPatoBrowser()) {
      this.patolarService.callOpenSafariViewer(url, callback);
    } else {
      if (AppConfig.IS_BROWSER || this.platform.is("android")) {
        // Web版＆Android版はこちらのルート
        location.href = url;
      } else {
        this.showSafariView(url, callback);
      }
    }
  }

  showSbPaymentSecureRedirectPage(
    redirectUrl: string,
    callback: () => void = () => {}
  ): void {
    if (Util().isPatolar() || Util().isPatoBrowser()) {
      this.patolarService.callOpenSafariViewer(redirectUrl, callback);
    } else {
      if (AppConfig.IS_BROWSER || this.platform.is("android")) {
        // Web版＆Android版はこちらのルート
        location.href = redirectUrl;
      } else {
        this.showSafariView(redirectUrl, callback);
      }
    }
  }

  showPaddyStore(): void {
    if (this.platform.is("ios")) {
      window.open(
        "itms-apps://itunes.apple.com/jp/app/id1321349856",
        "_system"
      );
    }
  }

  showByExternalBrowser(url: string): void {
    window.open(url, "_system");
  }

  showByBlankBrowser(url: string): void {
    window.open(url, "_blank");
  }

  getAxesUrl(userId: number): string {
    const clientip: string = AppConfig.AXES_CLIENTIP;
    return (
      "https://gw.axes-payment.com/cgi-bin/credit/order.cgi?clientip=" +
      clientip +
      "&sendid=" +
      userId +
      "&sendpoint=register"
    );
  }

  async showDownloadPaddy() {
    await this.showSafariView("https://itunes.apple.com/jp/app/id1321349856");
  }

  showPilatesNotionPage() {
    this.showSafariView(
      "https://patoinformation.notion.site/pato-94343388b98b454ba0c1be5964921c39"
    );
  }

  showFacilitiesNotionPage() {
    this.showSafariView(
      "https://notify-news.pato.today/a3ce8df286794293a0b22c1a424aba06"
    );
  }

  async showFebruaryEventCastPage(): Promise<void> {
    await this.showSafariView(
      "https://notify-news.pato.today/2f6a3d187c5b8085bd8dcf692744eab6"
    );
  }

  showAxesWithSafari(userId: number, callback: () => void): void {
    const url: string = this.getAxesUrl(userId);
    if (AppConfig.IS_BROWSER) {
      this.iab.open(url, "_inapp", "location=yes", callback);
      return;
    } else {
      this.showSafariView(url, () => callback());
    }
  }

  showReciept(history: PurchasePointHistory): void {
    const accessKey = history.recieptAccessKey;
    this.showSafariView(
      this.META_DATA.serverURL + "reciepts/" + accessKey + "/reciept.pdf"
    );
  }

  async showAutoLinkPage(url: string): Promise<void> {
    await this.showSafariView(url);
  }

  async showLineLink(LINE_LINK: string = AppConfig.LINE_LINK): Promise<void> {
    // ロケーションIDによってラインリンクを変える対応
    const url = "https://line.me/R/ti/p/%40" + LINE_LINK;
    await this.showByUrl(url);
  }

  getLineLink(): string {
    return "https://line.me/R/ti/p/%40" + AppConfig.LINE_LINK;
  }

  async showByUrl(url: string): Promise<void> {
    await this.showSafariView(url);
  }

  async showSafariView(url: string, callback: () => void = () => {}) {
    if (AppConfig.IS_BROWSER) {
      try {
        const win = window.open(url, "_system");
        win.addEventListener("exit", callback);
      } catch (e) {
        console.log(e);
      }
      return;
    } else {
      await Browser.open({
        url: url,
      });
      Browser.addListener("browserFinished", () => {
        console.log("Closed");
        Browser.removeAllListeners();
        callback();
      });
    }
  }

  showPhoneNumber(state = "", redirect = "") {
    window.open(
      "https://www.accountkit.com/v1.0/basic/dialog/sms_login/?app_id=" +
        ACCOUNTKIT_APP_ID +
        "&redirect=" +
        encodeURIComponent(REDIRECT_URL + redirect) +
        "&fbAppEventsEnabled=true&" +
        "state=" +
        state,
      "_self"
    );
  }
}
